import { useState, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import { generarCuotas, validarSumaCuotas } from "../../utils/cuotasUtil";
import {
  getPlanPagosActivos,
  crearPlanPagosActivos,
  actualizarCuotaActivo,
  eliminarCuotaActivo,
} from "../../utils/planPagosActivos";
import { client } from "../../utils/supabase";

// Plan de pagos en cuotas de un activo CapEx planificado (menú ⋮ de la fila CapEx, 4.2.mm).
// CAJA PURA (doctrina 4.19.1 / 10.100): escribe SOLO plan_pagos_activos; NO toca estado,
// fecha_compra, fecha_planificada ni la depreciación del activo. El total es activos.costo_unitario
// en activos.moneda (mismo total que proyecta mapCapex antes de convertir moneda). Guardado
// SELECTIVO: nunca toca cuotas pagadas (blindaje a prueba de futuro; hoy no existen aún).

const HOY = new Date().toISOString().split("T")[0];
const esPagada = (c) => c.estado === "pagada";

const normalizarFecha = (f) => (f && String(f).includes("T") ? String(f).substring(0, 10) : f || "");

// Acepta dos formas de entrada, sin romper ninguna:
//  · Caja (FlujoCajaV2): prop `entry` (la fila de proyección) → activo_id + razon_social_receptor.
//  · Edificios (modal Inversión): props directas `activoId` + `concepto`.
// En ambos casos el total/moneda salen de activos.costo_unitario (el modal busca el activo por id).
const PlanPagosActivoModal = ({ show, onHide, entry, activoId, concepto, userId, onSaved }) => {
  const resolvedActivoId = activoId ?? entry?.activo_id;
  const resolvedConcepto = concepto ?? entry?.razon_social_receptor ?? "—";

  const [cargando, setCargando] = useState(true);
  const [activo, setActivo] = useState(null); // { costo_unitario, moneda, pais }
  const [cuotas, setCuotas] = useState([]);
  const [cuotasOriginales, setCuotasOriginales] = useState([]);
  const [numCuotas, setNumCuotas] = useState(4);
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [fechaInicio, setFechaInicio] = useState(HOY);
  const [guardando, setGuardando] = useState(false);

  // Al abrir: trae el activo (total + moneda + país) y su plan existente. El modal se monta fresco
  // en cada apertura (render condicional en el padre), así que basta un efecto de montaje.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data, error } = await client
          .from("activos")
          .select("id, costo_unitario, moneda, pais")
          .eq("id", resolvedActivoId)
          .single();
        if (error) throw error;
        const plan = await getPlanPagosActivos(resolvedActivoId);
        if (!vivo) return;
        setActivo(data);
        const normalizadas = plan.map((c) => ({
          ...c,
          monto_cuota: Number(c.monto_cuota),
          fecha_vencimiento: normalizarFecha(c.fecha_vencimiento),
        }));
        setCuotas(normalizadas);
        setCuotasOriginales(normalizadas);
      } catch (e) {
        toast.error(`Error al cargar el plan: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!resolvedActivoId) return null;

  const total = Number(activo?.costo_unitario || 0);
  const monedaActivo = activo?.moneda === "USD" ? "USD" : activo?.pais === "Chile" ? "CLP" : "PEN";
  const simbolo = monedaActivo === "USD" ? "US$" : activo?.pais === "Chile" ? "$" : "S/";
  const fmt = (n) => `${simbolo} ${Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

  const sumaCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const cuadra = validarSumaCuotas(cuotas, total);
  const puedeGuardar = !cargando && !guardando && cuotas.length > 0 && cuadra;

  // Generador de conveniencia: prellena las cuotas PENDIENTES sobre el monto restante (total − pagadas),
  // conservando las pagadas. Cada celda queda editable después.
  const handleGenerar = () => {
    const n = Math.trunc(Number(numCuotas));
    if (!Number.isFinite(n) || n < 1) return toast.error("Ingresá al menos 1 cuota");
    if (!fechaInicio) return toast.error("Elegí una fecha de inicio");
    const pagadas = cuotas.filter(esPagada);
    const restante = total - pagadas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
    const nuevas = generarCuotas(restante, n, fechaInicio, frecuencia).map((c) => ({ ...c, estado: "pendiente" }));
    setCuotas([...pagadas, ...nuevas].map((c, i) => ({ ...c, numero_cuota: i + 1 })));
  };

  const handleCambiarCuota = (idx, field, value) => {
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleAgregarFila = () => {
    setCuotas((prev) => [
      ...prev,
      { numero_cuota: prev.length + 1, monto_cuota: 0, fecha_vencimiento: HOY, estado: "pendiente" },
    ]);
  };

  const handleQuitarFila = (idx) => {
    setCuotas((prev) => prev.filter((_, i) => i !== idx));
  };

  // Guardado SELECTIVO (espejo de guardarCuotasPagoSelectivo): UPDATE pendientes con cambios,
  // INSERT pendientes nuevas (numero_cuota = max existente + secuencial, respeta UNIQUE), DELETE
  // pendientes quitadas. Las pagadas NUNCA se tocan.
  const handleGuardar = async () => {
    if (cuotas.length === 0) return toast.error("Agregá al menos una cuota");
    if (!cuadra) return toast.error(`Las cuotas (${fmt(sumaCuotas)}) no suman el total (${fmt(total)})`);

    setGuardando(true);
    try {
      const pendientesState = cuotas.filter((c) => !esPagada(c));
      const originalesPendientes = cuotasOriginales.filter((c) => !esPagada(c));
      const maxNumero = cuotasOriginales.reduce((m, c) => Math.max(m, Number(c.numero_cuota) || 0), 0);
      const ops = [];

      // UPDATE: pendientes con id, solo si cambió fecha o monto.
      for (const c of pendientesState) {
        if (!c.id) continue;
        const orig = cuotasOriginales.find((o) => o.id === c.id);
        const cambios = {};
        if (orig && String(orig.fecha_vencimiento) !== String(c.fecha_vencimiento)) {
          cambios.fecha_vencimiento = c.fecha_vencimiento;
        }
        if (orig && Number(orig.monto_cuota) !== Number(c.monto_cuota)) {
          cambios.monto_cuota = Number(c.monto_cuota);
        }
        if (Object.keys(cambios).length > 0) ops.push(actualizarCuotaActivo(c.id, cambios));
      }

      // INSERT: pendientes nuevas (sin id). numero_cuota estable = max existente + secuencial.
      const nuevas = pendientesState.filter((c) => !c.id);
      if (nuevas.length > 0) {
        ops.push(
          crearPlanPagosActivos(
            nuevas.map((c, idx) => ({
              pais: activo.pais,
              activo_id: resolvedActivoId,
              numero_cuota: maxNumero + idx + 1,
              monto_cuota: Number(c.monto_cuota),
              fecha_vencimiento: c.fecha_vencimiento,
              estado: "pendiente",
              created_by: userId,
            }))
          )
        );
      }

      // DELETE: pendientes que estaban en DB y el usuario quitó (guard en el util: solo pendientes).
      const idsEnState = new Set(pendientesState.filter((c) => c.id).map((c) => c.id));
      for (const o of originalesPendientes) {
        if (o.id && !idsEnState.has(o.id)) ops.push(eliminarCuotaActivo(o.id));
      }

      await Promise.all(ops);
      toast.success("Plan de pagos guardado");
      onSaved?.();
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Plan de pagos del CapEx</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 12 }}>
          <div>
            <strong>Concepto:</strong> {resolvedConcepto}
          </div>
          <div>
            <strong>Monto total:</strong> {cargando ? "…" : `${fmt(total)} (${monedaActivo})`}
          </div>
          <div style={{ color: "#888", fontSize: "0.8rem" }}>
            Las cuotas van en la moneda del activo ({monedaActivo}).
          </div>
        </div>

        {/* Generador de conveniencia */}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
            flexWrap: "wrap",
            padding: "10px 12px",
            background: "#f8f9fa",
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          <Form.Group>
            <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>N° cuotas</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={numCuotas}
              onChange={(e) => setNumCuotas(e.target.value)}
              disabled={guardando}
              style={{ width: 90 }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Frecuencia</Form.Label>
            <Form.Select
              value={frecuencia}
              onChange={(e) => setFrecuencia(e.target.value)}
              disabled={guardando}
              style={{ width: 130 }}
            >
              <option value="semanal">Semanal</option>
              <option value="quincenal">Quincenal</option>
              <option value="mensual">Mensual</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Fecha inicio</Form.Label>
            <Form.Control
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              disabled={guardando}
              style={{ width: 160 }}
            />
          </Form.Group>
          <Button variant="outline-secondary" size="sm" onClick={handleGenerar} disabled={cargando || guardando}>
            Generar cuotas
          </Button>
        </div>

        {/* Tabla editable de cuotas */}
        <Table size="sm" bordered style={{ fontSize: "0.85rem", marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Vencimiento</th>
              <th>Monto ({monedaActivo})</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {cuotas.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", color: "#888" }}>
                  Sin cuotas. Generá o agregá filas.
                </td>
              </tr>
            ) : (
              cuotas.map((c, idx) => {
                const bloqueada = esPagada(c);
                return (
                  <tr key={c.id || `nueva-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>
                      <Form.Control
                        type="date"
                        size="sm"
                        min={HOY}
                        value={normalizarFecha(c.fecha_vencimiento)}
                        onChange={(e) => handleCambiarCuota(idx, "fecha_vencimiento", e.target.value)}
                        disabled={bloqueada || guardando}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={c.monto_cuota}
                        onChange={(e) => handleCambiarCuota(idx, "monto_cuota", e.target.value)}
                        disabled={bloqueada || guardando}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {bloqueada ? (
                        <span style={{ color: "#888", fontSize: "0.75rem" }}>pagada</span>
                      ) : (
                        <Button
                          variant="link"
                          size="sm"
                          style={{ color: "#c62828", padding: 0, textDecoration: "none" }}
                          onClick={() => handleQuitarFila(idx)}
                          disabled={guardando}
                        >
                          ✕
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button variant="outline-secondary" size="sm" onClick={handleAgregarFila} disabled={cargando || guardando}>
            + Agregar cuota
          </Button>
          <div style={{ fontSize: "0.9rem", color: cuadra ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
            Σ cuotas: {fmt(sumaCuotas)} / {fmt(total)}
            {!cuadra && <span style={{ marginLeft: 8, fontWeight: 400 }}>(no cuadra)</span>}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={!puedeGuardar}>
          {guardando ? "Guardando…" : "Guardar plan"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanPagosActivoModal;
